// Finance — Transactions (+ object drawer, Bank view) · Reports
import { useState } from "react";
import posthog from "@/lib/posthog";
import { Icon } from "@/components/Icon";
import { AiSummary, SectionHead, Search, Segmented, Tabs, Avatar } from "@/components/primitives";
import { useNotImplemented } from "@/hooks/use-not-implemented";
import { fmtUSD } from "@/utils/format";
import { DATA, Txn } from "@/data";

const STATUS_STYLE: Record<string, string> = {
  Open: "b-info", Approved: "b-brand", Overdue: "b-neg", Reconciled: "b-mute", Cleared: "b-pos",
  Scheduled: "b-warn", Posted: "b-mute", Paid: "b-pos", Partial: "b-violet", Pending: "b-warn",
  Unmatched: "b-neg", Draft: "b-mute",
};
const TYPE_ICON: Record<string, string> = { Invoice: "receipt", Bill: "doc", Expense: "wallet", Payment: "dollar", Bank: "bank", Journal: "book" };
const KTONE: Record<string, string> = { info: "var(--info)", pos: "var(--pos)", neg: "var(--neg)", warn: "var(--warn)", brand: "var(--brand)", ink: "var(--ink)" };

/* ═══ BANK — aggregated view across accounts & cards ════════════════ */
const BANK_STATUS: Record<string, string> = { Matched: "b-pos", Review: "b-warn", Pending: "b-mute" };
const fmtBank = (n: number) => (n < 0 ? "−" : "+") + "$" + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BankView = () => {
  const notify = useNotImplemented();
  const [acct, setAcct] = useState("all");
  const [view, setView] = useState("table");
  const accts = DATA.bankAccounts;
  const totalReview = accts.reduce((s, a) => s + a.review, 0);
  const rows = DATA.bankLedger.filter((r) => acct === "all" || r.acct === acct);
  const acctName = (id: string) => accts.find((a) => a.id === id)?.name || "";
  const reviewCount = acct === "all" ? totalReview : accts.find((a) => a.id === acct)?.review || 0;
  return (
    <div>
      {/* Connected accounts strip */}
      <div className="eyebrow" style={{ marginBottom: 10 }}>Connected accounts</div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${accts.length + 1},1fr)`, gap: 12, marginBottom: 16 }}>
        <button onClick={() => { posthog.capture("bank_account_selected", { account: "all" }); setAcct("all"); }} className="card" style={{
          padding: "14px 16px", textAlign: "left", cursor: "pointer",
          border: acct === "all" ? "1.5px solid var(--brand)" : "1px solid var(--line)",
          background: acct === "all" ? "var(--brand-tint)" : "var(--surface)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, background: "var(--brand)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="layers" size={16} stroke="#fff" />
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>All accounts</span>
          </div>
          <div className="num" style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-.02em" }}>{fmtUSD(accts.reduce((s, a) => s + a.bal, 0))}</div>
          <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 2 }}>{accts.length} connected</div>
        </button>
        {accts.map((a) => (
          <button key={a.id} onClick={() => { posthog.capture("bank_account_selected", { account: a.name }); setAcct(a.id); }} className="card" style={{
            padding: "14px 16px", textAlign: "left", cursor: "pointer", position: "relative",
            border: acct === a.id ? "1.5px solid var(--brand)" : "1px solid var(--line)",
            background: acct === a.id ? "var(--brand-tint)" : "var(--surface)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
              <span style={{ width: 30, height: 30, borderRadius: 8, background: a.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>{a.kind}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</span>
              {a.review > 0 && <span style={{ marginLeft: "auto", minWidth: 19, height: 19, padding: "0 5px", borderRadius: 10, background: "var(--warn-soft)", color: "#A16207", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{a.review}</span>}
            </div>
            <div className="num" style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-.02em", color: a.bal < 0 ? "var(--neg)" : "var(--ink)" }}>
              {a.bal < 0 ? "−$" + Math.abs(a.bal).toLocaleString() : fmtUSD(a.bal)}
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 2 }}>•• {a.last}</div>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <Segmented value={view} onChange={(v) => { posthog.capture("bank_view_changed", { view: v }); setView(v); }} options={[{ value: "table", label: "Table" }, { value: "statement", label: "Statement" }]} />
        <button className="btn btn-sec btn-sm" onClick={() => { posthog.capture("bank_date_filter_clicked"); notify("Date filter"); }}><Icon name="calendar" size={14} stroke="var(--ink-3)" /> Apr 7 – May 7</button>
        <button className="btn btn-sec btn-sm" onClick={() => { posthog.capture("bank_direction_filter_clicked"); notify("Direction filter"); }}><Icon name="sort" size={14} stroke="var(--ink-3)" /> Direction: All</button>
        <button className="btn btn-pri" style={{ marginLeft: "auto" }} onClick={() => { posthog.capture("bank_reconcile_clicked", { pending: reviewCount }); notify("Reconcile"); }}><Icon name="check" size={15} stroke="#fff" /> Reconcile · {reviewCount}</button>
      </div>

      {/* Ledger table */}
      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead><tr>
              <th style={{ width: 34 }}></th><th style={{ width: 80 }}>Date</th><th>Merchant</th>
              {acct === "all" && <th>Account</th>}
              <th>Category</th><th>Reference</th><th style={{ textAlign: "right" }}>Amount</th><th style={{ textAlign: "right" }}>Balance</th><th style={{ width: 90 }}>Status</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => {
                const rev = r.status === "Review";
                return (
                  <tr key={i} style={rev ? { background: "var(--warn-soft)", boxShadow: "inset 3px 0 0 var(--warn)" } : {}}>
                    <td>{r.status === "Matched"
                      ? <span style={{ width: 20, height: 20, borderRadius: 50, background: "var(--pos-soft)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon name="check" size={13} stroke="var(--pos)" sw={2.4} /></span>
                      : <span style={{ width: 18, height: 18, borderRadius: 50, border: "2px solid var(--warn)", display: "inline-block" }} />}</td>
                    <td style={{ color: "var(--ink-3)" }}>{r.date}</td>
                    <td style={{ fontWeight: 600, color: "var(--ink)" }}>{r.merchant}</td>
                    {acct === "all" && <td style={{ color: "var(--ink-3)", fontSize: 12 }}>{acctName(r.acct)}</td>}
                    <td><span className="chip" style={{ height: 22 }}>{r.cat}</span></td>
                    <td><span className="mono" style={{ fontSize: 11.5, color: "var(--ink-4)" }}>{r.ref}</span></td>
                    <td style={{ textAlign: "right" }}><span className="num" style={{ fontWeight: 600, color: r.amt < 0 ? "var(--neg)" : "var(--pos)" }}>{fmtBank(r.amt)}</span></td>
                    <td style={{ textAlign: "right" }}><span className="num" style={{ color: "var(--ink-3)" }}>{r.bal < 0 ? "−$" + Math.abs(r.bal).toLocaleString() : "$" + r.bal.toLocaleString()}</span></td>
                    <td><span className={"badge " + BANK_STATUS[r.status]}>{r.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderTop: "1px solid var(--line-2)" }}>
          <span style={{ fontSize: 12, color: "var(--ink-4)" }}>{rows.length} of 412 transactions · {reviewCount} to reconcile</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => { posthog.capture("bank_page_changed", { direction: "prev" }); notify("Previous page"); }}><Icon name="chevright" size={15} stroke="var(--ink-4)" style={{ transform: "rotate(180deg)" }} /></button>
            <span style={{ fontSize: 12, color: "var(--ink-3)" }}>Page 1 of 42</span>
            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => { posthog.capture("bank_page_changed", { direction: "next" }); notify("Next page"); }}><Icon name="chevright" size={15} stroke="var(--ink-3)" /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TxnKpiStrip = ({ tab }: { tab: string }) => {
  const kpis = DATA.txnKpis[tab] || DATA.txnKpis.All;
  return (
    <div className="card" style={{ padding: 0, marginBottom: 14, display: "flex", overflowX: "auto" }}>
      {kpis.map((k, i) => (
        <div key={i} style={{ flex: "1 0 0", minWidth: 150, padding: "14px 18px", borderRight: i < kpis.length - 1 ? "1px solid var(--line-2)" : "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span className="eyebrow" style={{ fontSize: 10.5 }}>{k[0]}</span>
            {k[4] && <span className="badge b-pos" style={{ height: 15, padding: "0 5px", fontSize: 9 }}>
              <span className="dot live-dot" style={{ width: 5, height: 5, background: "var(--pos)" }} />LIVE
            </span>}
          </div>
          <div className="num" style={{ fontSize: 21, fontWeight: 600, letterSpacing: "-.02em", color: KTONE[k[2] as string] }}>{k[1]}</div>
          <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 2 }}>{k[3]}</div>
        </div>
      ))}
    </div>
  );
};

type Reviewable = { review?: boolean; reviewNote?: string; aiConf?: number };

export const FinTxns = () => {
  const notify = useNotImplemented();
  const [tab, setTab] = useState("All");
  const [sel, setSel] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const reviewCount = DATA.txns.filter((t) => (t as Reviewable).review).length;
  const tabs = ["All", { value: "Needs Review", label: "Needs Review", badge: reviewCount }, "Invoices", "Bills", "Payments", "Expenses", "Bank", "Journal"];
  const map: Record<string, string> = { Invoices: "Invoice", Bills: "Bill", Payments: "Payment", Expenses: "Expense", Bank: "Bank", Journal: "Journal" };
  const rows = DATA.txns.filter((t) =>
    (tab === "All" || (tab === "Needs Review" ? !!(t as Reviewable).review : t.type === map[tab])) &&
    (t.party.toLowerCase().includes(query.toLowerCase()) || t.id.toLowerCase().includes(query.toLowerCase())));
  const item = DATA.txns.find((t) => t.id === sel);
  return (
    <div style={{ maxWidth: 1160, margin: "0 auto" }} className="fade-up">
      <SectionHead title="Transactions" size="lg"
        sub="Every accounting object lives here — invoices, bills, payments, expenses, bank lines and journal entries."
        actions={<>
          <button className="btn btn-sec" onClick={() => { posthog.capture("finance_export_clicked", { page: "transactions" }); notify("Export"); }}><Icon name="download" size={15} stroke="var(--ink-3)" /> Export</button>
          <button className="btn btn-pri" onClick={() => { posthog.capture("txn_new_clicked"); notify("New transaction"); }}><Icon name="plus" size={15} stroke="#fff" /> New</button>
        </>} />
      <AiSummary action={<button className="btn btn-sec btn-sm" onClick={() => { posthog.capture("txn_review_now_clicked", { pending: reviewCount }); setTab("Needs Review"); }}>Review now</button>}>
        Books are <b>99.2% reconciled</b> — everything matched and categorized automatically except <b>{reviewCount} items</b> drafted
        for your review. Three overdue invoices worth <b className="num">$24,310</b> are the only records needing a chase this week.
      </AiSummary>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <div style={{ overflowX: "auto" }}><Tabs value={tab} onChange={(v) => { posthog.capture("txn_tab_changed", { tab: v }); setTab(v); }} tabs={tabs} /></div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Search placeholder="Search records" w={220} value={query} onChange={(v) => { if (v && !query) posthog.capture("txn_search_used"); setQuery(v); }} />
          <button className="btn btn-sec btn-icon" onClick={() => { posthog.capture("txn_filter_clicked"); notify("Filters"); }}><Icon name="filter" size={15} stroke="var(--ink-3)" /></button>
        </div>
      </div>
      <TxnKpiStrip tab={tab} />
      {tab === "Bank" ? <BankView /> : (
        <div className="card" style={{ overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead><tr>
                <th style={{ width: 120 }}>ID</th><th>Party</th><th>Type</th><th>Category</th><th>Date</th><th>Status</th>
                <th style={{ textAlign: "right" }}>Amount</th><th style={{ width: 90 }}>Activity</th>
              </tr></thead>
              <tbody>
                {rows.map((t) => {
                  const rv = t as Reviewable;
                  const inReview = tab === "Needs Review";
                  return (
                  <tr key={t.id} onClick={() => { posthog.capture("txn_opened", { id: t.id, type: t.type, status: t.status }); setSel(t.id); }} style={
                    sel === t.id ? { background: "var(--brand-tint)" }
                      : inReview ? { background: "var(--warn-soft)", boxShadow: "inset 3px 0 0 var(--warn)" } : {}}>
                    <td><span className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>{t.id}</span></td>
                    <td style={{ fontWeight: 500, color: "var(--ink)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
                        <span style={{ width: 26, height: 26, borderRadius: 7, background: "var(--surface-3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Icon name={TYPE_ICON[t.type]} size={14} stroke="var(--ink-3)" />
                        </span>
                        <span>
                          {t.party}
                          {inReview && rv.reviewNote && (
                            <span style={{ display: "block", fontSize: 11.5, fontWeight: 400, color: "#A16207", marginTop: 2, maxWidth: 380 }}>
                              {rv.reviewNote} · AI {rv.aiConf}%
                            </span>
                          )}
                        </span>
                        {!inReview && rv.review && (
                          <span className="badge b-warn" title={rv.reviewNote} style={{ flexShrink: 0 }}>Review</span>
                        )}
                      </span>
                    </td>
                    <td>{t.type}</td>
                    <td><span className="chip" style={{ height: 22 }}>{t.cat}</span></td>
                    <td style={{ color: "var(--ink-3)" }}>{t.date}</td>
                    <td><span className={"badge " + STATUS_STYLE[t.status]}>{t.status}</span></td>
                    <td style={{ textAlign: "right" }}><span className="num" style={{ fontWeight: 600, color: t.amount < 0 ? "var(--ink-2)" : "var(--pos)" }}>{fmtUSD(t.amount)}</span></td>
                    <td>
                      <span style={{ display: "inline-flex", gap: 9, fontSize: 11.5, color: "var(--ink-4)" }}>
                        {t.cm > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Icon name="comment" size={13} stroke="var(--ink-4)" />{t.cm}</span>}
                        {t.mn > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Icon name="at" size={13} stroke="var(--ink-4)" />{t.mn}</span>}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {item && <TxnDrawer item={item} onClose={() => setSel(null)} />}
    </div>
  );
};

/* Object drawer — every record supports comments/mentions/activity/assignment */
const TxnDrawer = ({ item, onClose }: { item: Txn; onClose: () => void }) => {
  const notify = useNotImplemented();
  return (
  <>
    <div onClick={onClose} className="fade-up" style={{ position: "fixed", inset: 0, background: "rgba(23, 49, 58,.32)", zIndex: 60 }} />
    <div className="fade-up" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 440, background: "var(--surface)", zIndex: 61, boxShadow: "var(--sh-lg)", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ width: 38, height: 38, borderRadius: 10, background: "var(--surface-3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name={TYPE_ICON[item.type]} size={19} stroke="var(--ink-2)" />
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{item.party}</div>
          <div className="mono" style={{ fontSize: 11.5, color: "var(--ink-4)" }}>{item.id} · {item.type}</div>
        </div>
        <button className="btn btn-ghost btn-icon" onClick={onClose}><Icon name="x" size={17} stroke="var(--ink-3)" /></button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 22 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>Amount</div>
            <div className="num" style={{ fontSize: 26, fontWeight: 600, color: item.amount < 0 ? "var(--ink)" : "var(--pos)" }}>{fmtUSD(item.amount)}</div>
          </div>
          <span className={"badge " + STATUS_STYLE[item.status]} style={{ height: 24 }}>{item.status}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
          {[["Date", item.date], ["Category", item.cat], ["Assignee", "Sarah Reyes"], ["Source", "Mercury sync"]].map((r) => (
            <div key={r[0]}>
              <div style={{ fontSize: 11, color: "var(--ink-4)", marginBottom: 2 }}>{r[0]}</div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{r[1]}</div>
            </div>
          ))}
        </div>
        <div className="card ai-surface" style={{ padding: 13, marginBottom: 20, display: "flex", gap: 10 }}>
          <span className="ai-mark" style={{ width: 22, height: 22 }}><Icon name="spark" size={12} stroke="#fff" /></span>
          <div style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5 }}>
            <b>AI:</b> {(item as Reviewable).reviewNote
              ? <>{(item as Reviewable).reviewNote} <b>({(item as Reviewable).aiConf}% confidence — please confirm.)</b></>
              : item.status === "Overdue"
                ? "5 days overdue — Globex paid late on 3 of last 4 invoices. Recommend a call, not email."
                : "Matched to bank feed and categorized automatically. No anomalies detected."}
          </div>
        </div>
        <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10 }}>Activity & comments</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {[["ML", "Maya Lin", "commented", "@Sarah can you confirm the payment terms before we chase?", "2h"],
            ["BB", "System", "logged", "Reminder auto-sent to accounts@globex.com", "1d"],
            ["JC", "James Cho", "assigned", "this record to Sarah Reyes", "2d"]].map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 10 }}>
              <Avatar name={a[0]} size={28} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5 }}><b>{a[1]}</b> <span style={{ color: "var(--ink-3)" }}>{a[2]}</span> <span style={{ color: "var(--ink-4)", fontSize: 11 }}>· {a[4]}</span></div>
                <div style={{ fontSize: 12.5, color: "var(--ink-2)", marginTop: 2, lineHeight: 1.5 }}>{a[3]}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ padding: 14, borderTop: "1px solid var(--line)", display: "flex", gap: 8 }}>
        <input placeholder="Comment or @mention…" className="focus-ring" style={{ flex: 1, height: 36, padding: "0 12px", borderRadius: 9, border: "1px solid var(--line)", font: "inherit", fontSize: 13, outline: "none" }} />
        <button className="btn btn-pri btn-icon" onClick={() => { posthog.capture("txn_comment_sent", { id: item.id, type: item.type }); notify("Comments"); }}><Icon name="send" size={15} stroke="#fff" /></button>
      </div>
    </div>
  </>
  );
};

/* ═══ FINANCE · REPORTS — AI reports first, then traditional ════════ */
const AI_REPORTS = [
  ["Executive Summary", "This week's performance, risks and wins — in plain language.", "Updated 2h ago"],
  ["Board Report", "Full deck: financials, KPIs, forecast and commentary.", "Last generated May 1"],
  ["Investor Update", "Monthly narrative with metrics your investors ask about.", "Last generated Apr 30"],
  ["Weekly Summary", "What changed, what agents did, what needs attention.", "Updated Mon 8:00am"],
] as const;

export const FinReports = () => {
  const notify = useNotImplemented();
  return (
  <div style={{ maxWidth: 1020, margin: "0 auto" }} className="fade-up">
    <SectionHead title="Reports" size="lg"
      sub="AI-written reports and traditional accounting statements — always current, generated from the live ledger."
      actions={<button className="btn btn-sec" onClick={() => { posthog.capture("reports_period_clicked"); notify("Report period"); }}><Icon name="calendar" size={15} stroke="var(--ink-3)" /> {DATA.company.fy}</button>} />
    <div className="eyebrow" style={{ marginBottom: 10 }}>AI Reports</div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 14, marginBottom: 26 }}>
      {AI_REPORTS.map((r, i) => (
        <div key={i} className="card ai-surface" style={{ padding: 18, cursor: "pointer", transition: "box-shadow .15s" }}
          onClick={() => { posthog.capture("report_opened", { report: r[0], kind: "ai" }); notify(r[0]); }}
          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--sh-md)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
            <span className="ai-mark" style={{ width: 24, height: 24, borderRadius: 7 }}><Icon name="spark" size={13} stroke="#fff" /></span>
            <span style={{ fontSize: 14.5, fontWeight: 600 }}>{r[0]}</span>
            <span className="badge b-brand" style={{ marginLeft: "auto" }}>AI</span>
          </div>
          <div style={{ fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.5, minHeight: 36 }}>{r[1]}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--line-2)" }}>
            <span className="link" style={{ fontSize: 12.5, fontWeight: 600 }} onClick={(e) => { e.stopPropagation(); posthog.capture("report_generate_clicked", { report: r[0] }); notify("Generate now"); }}>Generate now</span>
            <span className="link" style={{ fontSize: 12.5 }} onClick={(e) => { e.stopPropagation(); posthog.capture("report_view_last_clicked", { report: r[0] }); notify("View last"); }}>View last</span>
            <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--ink-4)" }}>{r[2]}</span>
          </div>
        </div>
      ))}
    </div>
    <div className="eyebrow" style={{ marginBottom: 10 }}>Traditional Reports</div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
      {([
        ["Profit & Loss", "Revenue, COGS, opex and net income.", "chart-bar", "var(--brand)"],
        ["Balance Sheet", "Assets, liabilities and equity.", "layers", "var(--info)"],
        ["Cash Flow Statement", "Operating, investing and financing.", "wallet", "var(--pos)"],
        ["Budget", "Actuals vs. budget by line item.", "target", "var(--violet)"],
        ["Tax Report", "Liability estimate and filings.", "doc", "var(--warn)"],
        ["Custom Report", "Build any statement you need.", "sliders", "var(--ink-3)"],
      ] as const).map((r, i) => (
        <div key={i} className="card" style={{ padding: 18, cursor: "pointer", transition: "box-shadow .15s,border-color .15s" }}
          onClick={() => { posthog.capture("report_opened", { report: r[0], kind: "traditional" }); notify(r[0]); }}
          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--sh-md)"; e.currentTarget.style.borderColor = "#C9DBE0"; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--line)"; }}>
          <span style={{ width: 40, height: 40, borderRadius: 10, background: r[3] + "16", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
            <Icon name={r[2]} size={20} stroke={r[3]} />
          </span>
          <div style={{ fontSize: 14.5, fontWeight: 600 }}>{r[0]}</div>
          <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 4, lineHeight: 1.5, minHeight: 36 }}>{r[1]}</div>
          <div style={{ display: "flex", gap: 14, marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--line-2)" }}>
            <span className="link" style={{ fontSize: 12.5 }} onClick={(e) => { e.stopPropagation(); posthog.capture("report_opened", { report: r[0], kind: "traditional", via: "open_link" }); notify("Open " + r[0]); }}>Open</span>
            <span onClick={(e) => { e.stopPropagation(); posthog.capture("report_pdf_downloaded", { report: r[0] }); notify(r[0] + " PDF export"); }} style={{ fontSize: 12.5, color: "var(--ink-4)", display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
              <Icon name="download" size={13} stroke="var(--ink-4)" /> PDF
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};
